import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Phone, MessageCircle, Check, MapPin, Clock, Users, Car, FileText, Calculator, Building } from 'lucide-react';
import { Button } from '../components/ui/button';
import { COMPANY_INFO } from '../mock';
import { InternalLinksSection } from '../components/InternalLinksSection';

const UnfallgutachtenFrankfurtPage = () => {
  const [formData, setFormData] = useState({ name: '', phone: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    await fetch(`${API_URL}/api/leads/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...formData, source: 'unfallgutachten-frankfurt-landing-page' })
    });
    alert('Vielen Dank! Wir melden uns schnellstmöglich.');
    setFormData({ name: '', phone: '', message: '' });
  };

  const faqs = [
    {
      q: "Wie schnell bekomme ich einen Termin in Frankfurt?",
      a: "In Frankfurt und dem Rhein-Main-Gebiet vergeben wir Termine meist innerhalb von 24-48 Stunden. Durch unser bundesweites Netzwerk haben wir auch im Großraum Frankfurt erfahrene Gutachter vor Ort. Bei dringenden Fällen – etwa nach einem Unfall auf der A5 oder dem Frankfurter Kreuz – können wir oft noch schneller reagieren."
    },
    {
      q: "Wer trägt die Kosten für ein Unfallgutachten in Frankfurt?",
      a: "Bei einem unverschuldeten Unfall übernimmt die gegnerische Haftpflichtversicherung die Kosten für das Gutachten vollständig. Sie müssen nicht in Vorleistung gehen. Wichtig: Sie haben das gesetzliche Recht auf freie Gutachterwahl – lassen Sie sich nicht zu einem Versicherungsgutachter drängen. Ein unabhängiger Gutachter arbeitet ausschließlich in Ihrem Interesse."
    },
    {
      q: "Welche Gebiete in Frankfurt und Umgebung deckt ihr ab?",
      a: "Wir decken ganz Frankfurt ab – alle Stadtteile von Sachsenhausen bis Höchst, von Bornheim bis zum Flughafen. Auch das gesamte Rhein-Main-Gebiet gehört zu unserem Einsatzgebiet: Offenbach, Hanau, Bad Homburg, Oberursel, Neu-Isenburg, Dreieich, Darmstadt, Wiesbaden und Mainz. Wir kommen dorthin, wo Ihr Fahrzeug steht."
    },
    {
      q: "Was ist bei einem Unfall auf dem Frankfurter Kreuz oder der A5 zu beachten?",
      a: "Das Frankfurter Kreuz ist einer der meistbefahrenen Autobahnknoten Europas – Unfälle sind hier leider alltäglich. Nach einem Autobahnunfall: Sichern Sie die Unfallstelle ab, dokumentieren Sie mit Fotos und rufen Sie uns an. Wir kommen zur Werkstatt oder zum Abstellort und erstellen dort das Gutachten. Je früher Sie uns kontaktieren, desto schneller können wir den Prozess starten."
    },
    {
      q: "Brauche ich bei einem Parkrempler in Frankfurt auch ein Gutachten?",
      a: "Bei kleinen Schäden unter 750€ reicht oft ein Kostenvoranschlag. Ab etwa 750-1.000€ empfehlen wir ein vollständiges Gutachten, da Sie dann zusätzlich Wertminderung und Nutzungsausfall geltend machen können. Rufen Sie uns kurz an – wir beraten Sie kostenlos und unverbindlich, ob sich ein Gutachten in Ihrem Fall lohnt."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Unfallgutachten Frankfurt | Kfz-Sachverständige Rhein-Main | Dein Gutachten Direkt</title>
        <meta name="description" content="Unfallgutachten in Frankfurt am Main und Rhein-Main. Über 10 Jahre Erfahrung, gerichtsfeste Gutachten. Termine in 24-48h. Kostenlos bei unverschuldetem Unfall. ☎ Jetzt anrufen!" />
        <meta name="keywords" content="Unfallgutachten Frankfurt, Kfz Gutachter Frankfurt, Sachverständiger Frankfurt, Gutachter Offenbach, Gutachter Hanau, Unfallgutachter Rhein-Main, Kfz Gutachten Frankfurt" />
        <link rel="canonical" href="https://www.dein-gutachten-direkt.de/unfallgutachten-frankfurt" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Dein Gutachten Direkt - Frankfurt am Main",
            "description": "Kfz-Sachverständigenbüro mit über 10 Jahren Erfahrung. Unabhängige Unfallgutachten in Frankfurt, Offenbach, Hanau und dem gesamten Rhein-Main-Gebiet.",
            "telephone": "+4941018471160",
            "email": "info@dein-gutachten-direkt.de",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Frankfurt am Main",
              "addressRegion": "Hessen",
              "addressCountry": "DE"
            },
            "areaServed": [
              {"@type": "City", "name": "Frankfurt am Main"},
              {"@type": "City", "name": "Offenbach am Main"},
              {"@type": "City", "name": "Hanau"},
              {"@type": "City", "name": "Bad Homburg vor der Höhe"},
              {"@type": "City", "name": "Oberursel"},
              {"@type": "City", "name": "Neu-Isenburg"},
              {"@type": "City", "name": "Dreieich"}
            ],
            "priceRange": "€€"
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": { "@type": "Answer", "text": faq.a }
            }))
          })}
        </script>
      </Helmet>

      <Header />

      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-gray-900 via-black to-gray-800 pt-28 pb-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-green/20 border border-brand-green/30 rounded-full mb-6">
                  <Building className="w-4 h-4 text-brand-green" />
                  <span className="text-brand-green font-semibold text-sm">Frankfurt & Rhein-Main</span>
                </div>

                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  Unfallgutachten
                  <br />
                  <span className="text-brand-green">in Frankfurt</span>
                </h1>
                
                <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                  Unabhängige Kfz-Sachverständige mit <strong className="text-white">über 10 Jahren Erfahrung</strong>. 
                  Gerichtsfeste Gutachten in der Finanzmetropole und dem gesamten Rhein-Main-Gebiet.
                </p>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Über 10 Jahre Erfahrung</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Gesamtes Rhein-Main-Gebiet</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Gerichtsfeste Gutachten</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Zusammenarbeit mit Anwälten</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Termine in 24-48h</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0" />
                    <span>Kostenlose Erstberatung</span>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <a href={`tel:${COMPANY_INFO.phone}`}>
                    <Button size="lg" className="w-full sm:w-auto bg-brand-green hover:bg-brand-green-dark text-white text-lg px-8 py-6 rounded-xl font-bold">
                      <Phone className="w-5 h-5 mr-2" />
                      Jetzt anrufen
                    </Button>
                  </a>
                  <a href={`https://wa.me/${COMPANY_INFO.whatsapp}`} target="_blank" rel="noopener noreferrer">
                    <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6 rounded-xl font-bold">
                      <MessageCircle className="w-5 h-5 mr-2" />
                      WhatsApp
                    </Button>
                  </a>
                </div>

                <p className="text-gray-400 text-sm mt-6">
                  Bei unverschuldetem Unfall: Kostenübernahme durch gegnerische Versicherung
                </p>
              </div>

              <div className="bg-white/5 backdrop-blur border border-white/10 rounded-3xl p-8">
                <h2 className="text-2xl font-bold text-white mb-6">Einsatzgebiet Frankfurt & Rhein-Main</h2>
                
                <div className="space-y-4 mb-8">
                  <div className="flex items-start gap-4">
                    <MapPin className="w-6 h-6 text-brand-green flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Abgedeckte Gebiete</p>
                      <p className="text-gray-400">Ganz Frankfurt + Rhein-Main-Gebiet</p>
                      <p className="text-gray-400">Offenbach, Hanau, Bad Homburg, Darmstadt</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Clock className="w-6 h-6 text-brand-green flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Terminvergabe</p>
                      <p className="text-gray-400">Meist innerhalb 24-48 Stunden</p>
                      <p className="text-gray-400">Bundesweites Gutachternetzwerk</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Users className="w-6 h-6 text-brand-green flex-shrink-0 mt-1" />
                    <div>
                      <p className="text-white font-semibold">Unser Team</p>
                      <p className="text-gray-400">Mehrere erfahrene Sachverständige</p>
                      <p className="text-gray-400">Hauptsitz Rellingen, bundesweit tätig</p>
                    </div>
                  </div>
                </div>

                <div className="bg-brand-green/20 border border-brand-green/30 rounded-xl p-4">
                  <p className="text-brand-green font-semibold text-sm">
                    <strong>Freie Gutachterwahl:</strong> Bei unverschuldetem Unfall dürfen Sie Ihren Gutachter selbst wählen. Die Versicherung muss die Kosten übernehmen – egal ob in Frankfurt oder bundesweit.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Über uns Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="text-brand-green font-semibold text-sm uppercase tracking-wider">Über uns</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">Ihr Kfz-Gutachter für Frankfurt am Main</h2>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed text-lg">
                <strong>Dein Gutachten Direkt</strong> ist Ihr unabhängiger Kfz-Sachverständiger für Frankfurt und das Rhein-Main-Gebiet. 
                Mit unserem Hauptsitz in Rellingen bei Hamburg und einem bundesweiten Netzwerk sind wir auch in der 
                <strong> Finanzmetropole am Main</strong> schnell für Sie da.
              </p>

              <p className="text-gray-700 leading-relaxed text-lg">
                Frankfurt bedeutet Verkehr: Das Frankfurter Kreuz ist einer der meistbefahrenen Autobahnknoten Europas, 
                und auch in der Stadt selbst – von der Zeil bis zum Flughafen – passieren täglich zahlreiche Unfälle. 
                Seit über <strong>10 Jahren</strong> helfen wir Geschädigten, ihre Ansprüche <strong>gerichtsfest</strong> zu dokumentieren.
              </p>

              <p className="text-gray-700 leading-relaxed text-lg">
                Unser Team arbeitet eng mit <strong>Fachanwälten für Verkehrsrecht</strong> in Frankfurt und der Region zusammen. 
                Bei Bedarf können wir Sie direkt an einen spezialisierten Anwalt weitervermitteln – kostenlos und unverbindlich.
              </p>

              <div className="bg-gray-50 rounded-2xl p-8 my-8">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Das zeichnet uns aus:</h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Unabhängig:</strong> Wir arbeiten ausschließlich für Sie – nicht für Versicherungen oder Werkstätten</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Erfahren:</strong> Über 10 Jahre Erfahrung in der Kfz-Schadensbewertung</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Gerichtsfest:</strong> Unsere Gutachten sind rechtssicher und vor Gericht verwendbar</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Anwaltsnetzwerk:</strong> Enge Zusammenarbeit mit Verkehrsrechtsanwälten in Frankfurt</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-brand-green flex-shrink-0 mt-1" />
                    <span><strong>Bundesweit:</strong> Wir sind in ganz Deutschland für Sie da</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Was beinhaltet unser Gutachten */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <span className="text-brand-green font-semibold text-sm uppercase tracking-wider">Leistungen</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-2">Was beinhaltet unser Gutachten?</h2>
              <p className="text-xl text-gray-600 mt-4">Alle Ansprüche dokumentieren – mehr als nur Reparaturkosten</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Calculator className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Reparaturkostenkalkulation</h3>
                </div>
                <p className="text-gray-700">Detaillierte Berechnung nach Herstellervorgaben: Arbeitslohn, Original-Ersatzteile, Lackierung und alle Nebenkosten. In Frankfurt oft mit höheren Stundenverrechnungssätzen.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Car className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Wertminderung</h3>
                </div>
                <p className="text-gray-700">Der merkantile Minderwert nach dem Unfall. Besonders bei den vielen Premiumfahrzeugen in Frankfurt oft 500-1.500€ oder mehr – auch nach perfekter Reparatur.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Nutzungsausfall</h3>
                </div>
                <p className="text-gray-700">Entschädigung für jeden Tag ohne Ihr Fahrzeug: 23€ (Kleinwagen) bis 175€ (Oberklasse). Bei 10 Tagen Werkstattzeit schnell 400-700€ zusätzlich.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Wiederbeschaffungswert</h3>
                </div>
                <p className="text-gray-700">Bei Totalschaden: Was kostet ein gleichwertiges Ersatzfahrzeug auf dem Frankfurter Markt? Wir recherchieren aktuelle Angebote in der Region.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <Calculator className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Restwertermittlung</h3>
                </div>
                <p className="text-gray-700">Der Verkaufswert des beschädigten Fahrzeugs bei Totalschaden. Wir holen mehrere Angebote ein für den bestmöglichen Preis.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 border-2 border-gray-200 hover:border-brand-green/50 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="w-6 h-6 text-brand-green" />
                  <h3 className="font-bold text-gray-900 text-lg">Fotodokumentation</h3>
                </div>
                <p className="text-gray-700">30-50 professionelle Fotos dokumentieren jeden Schaden. Diese Beweissicherung ist im Streitfall vor Gericht entscheidend.</p>
              </div>
            </div>

            <div className="bg-brand-green/10 border-2 border-brand-green/30 rounded-2xl p-6 mt-8 text-center">
              <p className="text-gray-900 font-semibold">
                <strong>Rechenbeispiel Frankfurt:</strong> Ein 3er BMW mit 5.000€ Reparaturkosten, 700€ Wertminderung und 10 Tagen Nutzungsausfall (590€) 
                = 1.290€ mehr als nur der Kostenvoranschlag!
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Häufige Fragen zu Unfallgutachten in Frankfurt</h2>
            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                  <h3 className="font-bold text-gray-900 mb-3 text-lg">{faq.q}</h3>
                  <p className="text-gray-700 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Stadtteile */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Frankfurt & Rhein-Main-Gebiet</h2>
            <p className="text-lg text-gray-600 mb-8">Alle Frankfurter Stadtteile und die gesamte Region</p>
            <div className="flex flex-wrap justify-center gap-3">
              {['Frankfurt-Innenstadt', 'Sachsenhausen', 'Bornheim', 'Bockenheim', 'Höchst', 'Nordend', 'Westend', 'Flughafen', 'Offenbach', 'Hanau', 'Bad Homburg', 'Oberursel', 'Neu-Isenburg', 'Dreieich', 'Darmstadt', 'Wiesbaden', 'Mainz', 'Rodgau'].map((ort) => (
                <span key={ort} className="px-4 py-2 bg-white rounded-lg border border-gray-200 text-gray-700 font-medium hover:border-brand-green/50 transition-colors">
                  {ort}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-gradient-to-br from-gray-900 to-black">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Jetzt Gutachten in Frankfurt beauftragen</h2>
            <p className="text-xl text-gray-300 mb-8">Frankfurt & Rhein-Main • Termine in 24-48 Stunden</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${COMPANY_INFO.phone}`}>
                <Button size="lg" className="bg-brand-green hover:bg-brand-green-dark text-white text-lg px-10 py-6 rounded-xl font-bold">
                  <Phone className="w-5 h-5 mr-2" />
                  04101 847 11 60
                </Button>
              </a>
              <a href={`https://wa.me/${COMPANY_INFO.whatsapp}`} target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 text-lg px-10 py-6 rounded-xl font-bold">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </section>

        <InternalLinksSection currentPage="/unfallgutachten-frankfurt" />
      </main>

      <Footer />
    </div>
  );
};

export default UnfallgutachtenFrankfurtPage;
